import React from "react";
import type { DataTableCardRole, DataTableColumn } from "./DataTable.js";

/* Slot resolution for the `responsive` card list — which column plays which
   part. Split from DataTableCards.tsx so that file stays rendering-only and both
   halves stay under the package's 200-line soft cap (§6). Pure and synchronous:
   no React state, so it is directly unit-testable. */

type SingleSlot = Exclude<DataTableCardRole, "meta">;

export interface CardSlots<Row> {
  readonly slots: Partial<Record<SingleSlot, DataTableColumn<Row>>>;
  readonly meta: ReadonlyArray<DataTableColumn<Row>>;
}

/**
 * Does this header render no visible text?
 *
 * A heuristic, and only a fallback: `<span className="sr-only">Actions</span>` is
 * the established way to head an action column, and treating it as a label would
 * print an empty `<dt>`. Callers who want certainty set `cardRole: "actions"`.
 */
export function hasSrOnlyHeader(header: React.ReactNode): boolean {
  if (!React.isValidElement(header)) return false;
  const { className } = header.props as { className?: unknown };
  return (
    typeof className === "string" && className.split(/\s+/).includes("sr-only")
  );
}

export function resolveCardColumns<Row>(
  columns: ReadonlyArray<DataTableColumn<Row>>,
): CardSlots<Row> {
  const slots: Partial<Record<SingleSlot, DataTableColumn<Row>>> = {};
  const claimed = new Set<DataTableColumn<Row>>();
  const undeclared: DataTableColumn<Row>[] = [];

  for (const col of columns) {
    if (col.cardRole === undefined) {
      undeclared.push(col);
    } else if (col.cardRole !== "meta" && slots[col.cardRole] === undefined) {
      slots[col.cardRole] = col;
      claimed.add(col);
    }
  }

  /*
    Zero-config: with no roles declared at all, the first column is the row's
    identity and an sr-only-headed column is its action menu. That makes every
    existing consumer read better without touching a call site.
  */
  if (slots.title === undefined) {
    const first = undeclared.shift();
    if (first !== undefined) {
      slots.title = first;
      claimed.add(first);
    }
  }
  if (slots.actions === undefined) {
    const index = undeclared.findIndex((col) => hasSrOnlyHeader(col.header));
    const [actions] = index === -1 ? [] : undeclared.splice(index, 1);
    if (actions !== undefined) {
      slots.actions = actions;
      claimed.add(actions);
    }
  }

  // Whatever is left keeps its declared column order.
  return { slots, meta: columns.filter((col) => !claimed.has(col)) };
}
